'use client';

import { memo, useEffect, useRef } from 'react';
import { RotateCwIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const VERSION_CHECK_ENDPOINT = '/internal-api/version-check';
const VERSION_CHECK_INTERVAL_MS = 60_000;
const CURRENT_BUILD_VERSION_STORAGE_KEY = 'musein:build-version:current';
const DISMISSED_BUILD_VERSION_STORAGE_KEY = 'musein:build-version:dismissed';
const LATEST_BUILD_VERSION_STORAGE_KEY = 'musein:build-version:latest';
const VERSION_UPDATE_TOAST_ID = 'version-update-toast';

interface VersionUpdateNotifierProps {
  buildVersion: string;
}

interface VersionCheckResponse {
  success?: boolean;
  data?: {
    latestVersion?: string;
  };
}

function normalizeVersion(version?: string | null) {
  const normalizedVersion = version?.trim();
  return normalizedVersion ? normalizedVersion : '';
}

function formatDisplayVersion(version: string) {
  const normalizedVersion = normalizeVersion(version);

  if (normalizedVersion.length <= 9) {
    return normalizedVersion;
  }

  return normalizedVersion.slice(0, 9);
}

function VersionUpdateNotifierComponent({ buildVersion }: VersionUpdateNotifierProps) {
  const { t } = useTranslation();
  const currentBuildVersion = normalizeVersion(buildVersion);
  const isCheckingRef = useRef(false);
  const isToastVisibleRef = useRef(false);

  useEffect(() => {
    if (!currentBuildVersion) {
      return;
    }

    localStorage.setItem(CURRENT_BUILD_VERSION_STORAGE_KEY, currentBuildVersion);

    if (localStorage.getItem(DISMISSED_BUILD_VERSION_STORAGE_KEY) === currentBuildVersion) {
      localStorage.removeItem(DISMISSED_BUILD_VERSION_STORAGE_KEY);
    }
  }, [currentBuildVersion]);

  useEffect(() => {
    if (!currentBuildVersion) {
      return;
    }

    let disposed = false;

    const dismissToast = () => {
      isToastVisibleRef.current = false;
      toast.dismiss(VERSION_UPDATE_TOAST_ID);
    };

    const showVersionUpdateToast = (latestVersion: string) => {
      if (isToastVisibleRef.current) {
        return;
      }

      isToastVisibleRef.current = true;
      const displayVersion = formatDisplayVersion(latestVersion);

      toast.custom(
        () => (
          <div
            className={cn(
              'pointer-events-auto flex w-max max-w-[calc(100vw-2rem)] items-center gap-6 rounded-xl border border-zinc-200 bg-white py-3 pl-4 pr-3',
              'dark:border-zinc-800 dark:bg-zinc-900'
            )}
          >
            <div className="flex min-w-0 flex-1 items-center">
              <div className="min-w-0">
                <p className="whitespace-nowrap text-sm font-medium leading-normal text-zinc-900 dark:text-zinc-100">
                  {t('versionUpdate.title', { version: displayVersion })}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                color="primary"
                size="sm"
                className="gap-1.5 px-3 text-[13px]"
                onClick={() => {
                  localStorage.removeItem(DISMISSED_BUILD_VERSION_STORAGE_KEY);
                  dismissToast();
                  window.location.reload();
                }}
                suffixIcon={RotateCwIcon}
              >
                {t('versionUpdate.refresh')}
              </Button>
              <button
                type="button"
                aria-label={t('common.close')}
                className={cn(
                  'flex size-8 items-center justify-center rounded-lg text-zinc-500 transition-colors',
                  'hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
                )}
                onClick={() => {
                  localStorage.setItem(DISMISSED_BUILD_VERSION_STORAGE_KEY, latestVersion);
                  dismissToast();
                }}
              >
                <XIcon className="size-4" />
              </button>
            </div>
          </div>
        ),
        {
          id: VERSION_UPDATE_TOAST_ID,
          duration: Infinity,
          dismissible: false,
          unstyled: true,
          className: cn(
            '!border-0 !bg-transparent !shadow-none !p-0 !ring-0',
            '!rounded-none overflow-visible'
          ),
          onDismiss: () => {
            isToastVisibleRef.current = false;
          },
        }
      );
    };

    const checkVersion = async () => {
      if (disposed || isCheckingRef.current || document.visibilityState === 'hidden') {
        return;
      }

      isCheckingRef.current = true;

      try {
        const response = await fetch(VERSION_CHECK_ENDPOINT, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as VersionCheckResponse;
        const latestVersion = normalizeVersion(payload.data?.latestVersion);

        if (!latestVersion) {
          return;
        }

        localStorage.setItem(LATEST_BUILD_VERSION_STORAGE_KEY, latestVersion);

        if (latestVersion === currentBuildVersion) {
          if (localStorage.getItem(DISMISSED_BUILD_VERSION_STORAGE_KEY) === latestVersion) {
            localStorage.removeItem(DISMISSED_BUILD_VERSION_STORAGE_KEY);
          }
          dismissToast();
          return;
        }

        if (localStorage.getItem(DISMISSED_BUILD_VERSION_STORAGE_KEY) === latestVersion) {
          return;
        }

        showVersionUpdateToast(latestVersion);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[VersionUpdateNotifier] version check failed', error);
        }
      } finally {
        isCheckingRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };

    void checkVersion();

    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, VERSION_CHECK_INTERVAL_MS);

    window.addEventListener('focus', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleVisibilityChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      dismissToast();
    };
  }, [currentBuildVersion, t]);

  return null;
}

export const VersionUpdateNotifier = memo(VersionUpdateNotifierComponent);
